import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { MapPin, Clock, Users, Star, Phone, Navigation } from 'lucide-react';
import Header from '../../components/ui/Header';

const TrailDetailsOnceUponATimeMythsAndLegendsOfMountainPeople = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Natural attractions data
  const naturalAttractions = [
  {
    title: "Bojovo brdo – planina nad Moravicom",
    description: "Sa Bojovog brda pogled se prostire preko doline reke Moravice sve do Kozjih stena i vrha Jankov kamen. Šume smrče, jela i bukvi obavijaju planinu kao veo, a u podnožju se čuje šum reke. U rano jutro magla se povlači iz doline, otkrivajući pejzaž koji izgleda isto kao pre stotinu godina. Ovo je mesto gde priroda diše polako, a tišina ima svoj zvuk.",
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458340/bojovo_brdo_3.1_lbiadt.jpg",
    imageAlt: "Pogled sa Bojovog brda preko doline reke Moravice"
  },
  {
    title: "Reka Moravica – mit o boginji vodi",
    description: "Moravica teče kao žila koja povezuje Goliju s dolinama zapadne Srbije. Njeno ime potiče od stare slovenske boginje More ili Morane, gospodarice vode, snega i smrti. Kažu da je nekada, u noćima bez meseca, Morana izlazila iz reke i šetala obalama, tražeći izgubljenu dušu. U narodu se verovalo da voda Moravice ima dvostruku prirodu – leči i kažnjava, donosi život i odnosi ga. Zato je svaka kap poštovana kao dar.",
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458328/3.2_WIKIPEDIA_uecyf0.jpg",
    imageAlt: "Reka Moravica sa kristalno čistom vodom"
  },
  {
    title: "Jankov kamen – pogled do pola Srbije",
    description: "Na najvišem vrhu Golije, na 1.833 metra, uzdiže se kamen koji nosi ime Sibinjanin Janka, junaka iz narodnih pesama. Legenda kaže da je Janko, bežeći posle Kosovske bitke, zastao na vrhu planine, podigao mač i rekao: „Ovde ću stati, jer nigde lepše nisam video.\" Sa Jankovog kamena vidi se pola Srbije – Pešter, Kopaonik, pa sve do Jadranskog mora kad je nebo čisto. To je mesto gde i najtiši ostaju bez reči.", image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458330/3.3foto_miloje_ostojic_lucqoc.jpg", imageAlt: "Jankov kamen na vrhu Golije sa panoramskim pogledom"
  },
  {
    title: "Dajićko jezero – zmajevo ogledalo", description: "Duboko u šumi, nedaleko od sela Dajići, nalazi se Dajićko jezero. Voda je tamna i mirna, a u njoj se, po predanju, nekada krio zmaj koji je noću napadao stoku. Stariji meštani i danas tvrde da se na površini povremeno pojavi oblak pare – znak da se zmaj pomerio u dubinu. Bez obzira na priču, jezero je danas tiho, spokojno i puno čarolije. U njegovom odrazu ogledaju se nebo, drveće i legende.", image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458334/3.4_2.jpg_mini_tox0ny.jpg", imageAlt: "Dajićko jezero okruženo gustom šumom"
  }];


  // Cultural heritage data
  const culturalHeritage = [
  {
    title: "Rimski most u Kumanici – spona vekova",
    description: "Na putu između sela Kumanica i reke Moravice, nalazi se Rimski most, star više od četiri veka. Zidali su ga stari majstori u vreme Osmanskog carstva, a kameni luk i danas stoji netaknut. Preko njega su prolazili putnici, karavani, pa i vojske. Danas, most mirno spaja obale, kao svedok vremena kada je sve bilo sporije i jednostavnije.",
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458343/rimski-most_reka_Moravica_3.5_miloje_ostojic_qcm3m9.jpg",
    imageAlt: "Rimski most u Kumanici preko reke Moravice"
  }];


  // Trail information
  const trailInfo = {
    length: "oko 11 km",
    duration: "4–5 sati hoda",
    difficulty: "srednje zahtevna",
    suitable: "ljubitelje prirode, planinare, porodice, učenike i grupe"
  };

  // Visit program
  const visitProgram = [
  "Polazak iz sela Kumanica",
  "Obilazak Rimskog mosta na Moravici",
  "Uspon na Bojovo brdo i pogled prema Kozjim stenama",
  "Odmor kod Dajićkog jezera",
  "Povratak kroz dolinu Moravice i poseta vodenicama u Reki"];


  // Accommodation data
  const accommodation = {
    title: "Vila Ivanović",
    address: "Kumanica bb, 32260 Ivanjica",
    phone: "+381 65 873 4432",
    gps: "43°29\'03\"N 20°20\'28\"E",
    description: "Vila Ivanović leži u podnožju Bojovog brda, uz reku Moravicu. Drvene sobe, široke terase i miris domaće kuhinje dočekaju svakog gosta. Uveče se uz ognjište prepričavaju legende o Sibinjanin Janku i vilama koje su plesale na Kozjim stenama. Domaćini su poznati po rakiji i domaćem siru.",
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458337/3.5-BIO_JEDNOM_JEDAN.jpg_mini_gekldu.jpg",
    imageAlt: "Vila Ivanović u podnožju Bojovog brda uz reku Moravicu"
  };

  // Must-see highlights
  const highlights = [
  "Jankov kamen (1.833 m) – vidikovac sa koga se vidi pola Srbije; najviši vrh Golije",
  "Rimski most i dolinu Moravice",
  "Dajićko jezero",
  "Bojovo brdo"];


  const tabs = [
  { id: 'overview', label: 'Pregled' },
  { id: 'attractions', label: 'Atrakcije' },
  { id: 'accommodation', label: 'Smeštaj' }];


  return (
    <>
      <Helmet>
        <title>Bio jednom jedan - Mitovi i legende ljudi sa planine | Creative Twinning</title>
        <meta
          name="description"
          content="Otkrijte čarobnu stazu kroz mitove i legende Golije. Na Bojovom brdu srećemo duhove prošlosti, hrabre junake i vile u stazi koja vodi kroz područje gde su se vekovima preplitale stvarnost i predanja." />

        <meta name="keywords" content="Golija, mitovi, legende, Bojovo brdo, Jankov kamen, Moravica, Dajićko jezero, srpsko kulturno nasleđe, planinski turizam" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <section className="relative h-96 bg-gradient-to-r from-blue-900 to-blue-700">
            <div
              className="absolute inset-0 bg-cover bg-center bg-blend-overlay bg-black bg-opacity-40"
              style={{ backgroundImage: `url('https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763458340/bojovo_brdo_3.1_lbiadt.jpg')` }} />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <div className="text-white max-w-3xl">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                  Bio jednom jedan
                </h1>
                <p className="text-xl md:text-2xl mb-6 text-blue-100">
                  Mitovi i legende ljudi sa planine
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span>Golija, Srbija</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{trailInfo?.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Navigation className="w-5 h-5" />
                    <span>{trailInfo?.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {/* Navigation Tabs */}
                <div className="flex flex-wrap gap-1 mb-8 bg-gray-100 p-1 rounded-lg">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                    activeTab === tab?.id ?
                    'bg-white text-primary shadow-sm' :
                    'text-gray-600 hover:text-gray-900'}`
                    }>

                      {tab?.label}
                    </button>
                  )}
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' &&
                <div className="space-y-8">
                    {/* Trail Description */}
                    <section>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">O stazi</h2>
                      <div className="prose prose-lg max-w-none text-gray-600">
                        <p className="text-lg leading-relaxed mb-6">
                          Na Goliji, svaka stena ima ime, a svaki potok – priču. Ovde vreme ne briše legende, već ih čuva u šumama, izvorima i kamenju. Staza „Bio jednom jedan – Mitovi i legende ljudi sa planine" vodi kroz područje Bojovog brda, gde su se vekovima preplitale stvarnost i predanja.
                        </p>
                        <p className="text-lg leading-relaxed">
                          Na ovom putu srećemo duhove prošlosti, hrabre junake, bogove i vile, ali i jednostavne ljude čija su srca bila veća od planine. Ova ruta nije samo staza kroz prirodu – to je povratak u vreme kada su priče bile istinite.
                        </p>
                      </div>
                    </section>

                    {/* Visit Program */}
                    <section>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Program obilaska</h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <ul className="space-y-3">
                          {visitProgram?.map((item, index) =>
                        <li key={index} className="flex items-start gap-3">
                              <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                                {index + 1}
                              </span>
                              <span className="text-gray-700">{item}</span>
                            </li>
                        )}
                        </ul>
                        <div className="mt-6 pt-4 border-t border-blue-200">
                          <p className="text-sm text-blue-700">
                            <strong>Najlepše vreme za posetu:</strong> proleće i rana jesen, kada reka teče najbistrije, a magle nad dolinama stvaraju pejzaže nalik snu.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Must See */}
                    <section>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Ne propustite</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {highlights?.map((highlight, index) =>
                      <div key={index} className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                            <Star className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-green-800 font-medium">{highlight}</span>
                          </div>
                      )}
                      </div>
                    </section>
                  </div>
                }

                {activeTab === 'attractions' &&
                <div className="space-y-8">
                    {/* Natural Attractions */}
                    <section>
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Prirodne atrakcije</h2>
                      <div className="space-y-8">
                        {naturalAttractions?.map((attraction, index) =>
                      <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                            <div className="md:flex">
                              <div className="md:w-1/3">
                                <img
                              src={attraction?.image}
                              alt={attraction?.imageAlt}
                              className="w-full h-48 md:h-full object-cover" />

                              </div>
                              <div className="md:w-2/3 p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{attraction?.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{attraction?.description}</p>
                              </div>
                            </div>
                          </div>
                      )}
                      </div>
                    </section>

                    {/* Cultural Heritage */}
                    <section>
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Kulturno nasleđe</h2>
                      <div className="space-y-8">
                        {culturalHeritage?.map((heritage, index) =>
                      <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                            <div className="md:flex">
                              <div className="md:w-1/3">
                                <img
                              src={heritage?.image}
                              alt={heritage?.imageAlt}
                              className="w-full h-48 md:h-full object-cover" />

                              </div>
                              <div className="md:w-2/3 p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{heritage?.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{heritage?.description}</p>
                              </div>
                            </div>
                          </div>
                      )}
                      </div>
                    </section>
                  </div>
                }

                {activeTab === 'accommodation' &&
                <div className="space-y-8">
                    <section>
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Gde jesti i prespavati</h2>
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg">
                        <div className="md:flex">
                          <div className="md:w-1/3">
                            <img
                            src={accommodation?.image}
                            alt={accommodation?.imageAlt}
                            className="w-full h-48 md:h-full object-cover" />

                          </div>
                          <div className="md:w-2/3 p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{accommodation?.title}</h3>
                            <div className="space-y-3 mb-4">
                              <div className="flex items-center gap-2 text-gray-600">
                                <MapPin className="w-4 h-4" />
                                <span>{accommodation?.address}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-4 h-4" />
                                <span>{accommodation?.phone}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Navigation className="w-4 h-4" />
                                <span>GPS: {accommodation?.gps}</span>
                              </div>
                            </div>
                            <p className="text-gray-600 leading-relaxed">{accommodation?.description}</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                }
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  {/* Trail Info Card */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Informacije o stazi</h3>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Navigation className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm text-gray-500">Dužina</p>
                          <p className="font-semibold text-gray-900">{trailInfo?.length}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm text-gray-500">Trajanje</p>
                          <p className="font-semibold text-gray-900">{trailInfo?.duration}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 text-primary font-bold">!</div>
                        <div>
                          <p className="text-sm text-gray-500">Težina</p>
                          <p className="font-semibold text-gray-900 capitalize">{trailInfo?.difficulty}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm text-gray-500">Preporučeno za</p>
                          <p className="font-semibold text-gray-900">{trailInfo?.suitable}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg p-6">
                    <h3 className="text-lg font-bold mb-4">Planirajte posetu</h3>
                    <div className="space-y-3">
                      <button className="w-full bg-blue-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-400 transition-colors duration-200 flex items-center justify-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Pogledaj na mapi
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default TrailDetailsOnceUponATimeMythsAndLegendsOfMountainPeople;